import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { DOMAIN_PARAM, domainOverrideEnabled } from '../api/fairDomain';
import { useFair } from '../api/FairProvider';
import { siblingFlagUrl } from '../utils/flag';

import { Modal } from './Modal';

export interface FairsModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Each country's fair is its own deployment, so every entry is normally an
 * external link opened in a new tab rather than an in-app route.
 *
 * TESTING ONLY: with `VITE_ALLOW_DOMAIN_OVERRIDE` set, entries instead point at
 * `/?domain=<host>` on this deployment, which repoints it at that fair. These
 * stay in the same tab on purpose — the selected fair is one value in storage,
 * so a second tab would silently be showing a fair it no longer targets.
 */
export function FairsModal({ open, onClose }: FairsModalProps): ReactElement {
  const { t } = useTranslation();
  const { siblings } = useFair();

  return (
    <Modal open={open} onClose={onClose} title={t('fairs.title')} subtitle={t('fairs.subtitle')}>
      <ul className="space-y-3">
        {siblings.map((fair) => {
          // Falls back to the external URL when a sibling has no domain stored,
          // since there would be nothing to put in `?domain=`.
          const overrideHref =
            domainOverrideEnabled && fair.domain !== null
              ? `/?${DOMAIN_PARAM}=${encodeURIComponent(fair.domain)}`
              : null;

          return (
            <li key={fair.id}>
              <a
                href={overrideHref ?? fair.url ?? '#'}
                {...(overrideHref === null && {
                  target: '_blank',
                  rel: 'noreferrer noopener',
                })}
                aria-current={fair.isCurrent ? 'true' : undefined}
                className={`flex items-center justify-between rounded-[var(--radius-card)] px-5 py-4 transition-colors ${
                  fair.isCurrent
                    ? 'bg-ink-700 text-fog-100'
                    : 'bg-ink-850 text-fog-500 hover:bg-ink-800'
                }`}
              >
                <span className="text-lg">{fair.name}</span>
                <img
                  src={siblingFlagUrl(fair)}
                  alt=""
                  className="size-10 rounded-full object-cover"
                />
              </a>
            </li>
          );
        })}
      </ul>
    </Modal>
  );
}
